// V4 AI Market Intelligence — server-built widget renderers.
// The backend guarantees every number here came from a real DB query;
// the frontend only renders.
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MapPin, TrendingUp, BarChart3, Home, Car, Bell, PanelRight } from "lucide-react";
import { isPlausibleYield } from "../../lib/yieldPlausibility";
import { SaveAlertDialog } from "../alerts/SaveAlertDialog";
import { MiniMapWidget } from "./widgets/MiniMapWidget";
import { PriceDistributionWidget } from "./widgets/PriceDistributionWidget";
import { ScatterPlotWidget } from "./widgets/ScatterPlotWidget";
import { ListingCompareWidget } from "./widgets/ListingCompareWidget";
import { ListingGroupsWidget } from "./widgets/ListingGroupsWidget";
import { VehicleImportWidget } from "./widgets/VehicleImportWidget";
import { CalculationWidget } from "./widgets/CalculationWidget";
import { DealComparablesChatWidget } from "./widgets/DealComparablesChatWidget";
import { ManualVehicleEntryCard } from "./ManualVehicleEntryCard";

// V4.8 P0 — broken-images policy: walk the healthy-candidate chain
// (primary → further gallery URLs) before showing a clear
// "Kein Bild verfügbar" placeholder — never a broken browser icon.
function ListingThumb({ it }) {
  const { t } = useTranslation("ai");
  const candidates = (it.image_candidates && it.image_candidates.length
    ? it.image_candidates
    : [it.primary_image_url]
  ).filter(Boolean);
  const [idx, setIdx] = useState(0);
  if (!candidates.length || idx >= candidates.length) {
    return (
      <div
        className="h-16 w-20 rounded-lg bg-zinc-800 flex flex-col items-center justify-center gap-0.5 flex-shrink-0"
        data-testid="ai-listing-no-image"
      >
        {it.vertical === "auto" ? (
          <Car className="h-4 w-4 text-zinc-600" />
        ) : (
          <Home className="h-4 w-4 text-zinc-600" />
        )}
        <span className="text-[8px] leading-tight text-zinc-600 text-center px-0.5">
          {t("widgets.noImage")}
        </span>
      </div>
    );
  }
  return (
    <img
      src={candidates[idx]}
      alt=""
      className="h-16 w-20 rounded-lg object-cover flex-shrink-0"
      loading="lazy"
      onError={() => setIdx(idx + 1)}
    />
  );
}

function fmtEur(v) {
  if (v == null || Number.isNaN(v)) return "—";
  return `€ ${Math.round(v).toLocaleString("de-AT")}`;
}

function SignalBadge({ signal, pct }) {
  const { t } = useTranslation("ai");
  if (!signal) return null;
  const under = signal === "under_market" || signal === "strong_under_market";
  const over = signal === "over_market" || signal === "strongly_over_market";
  if (!under && !over) return null;
  return (
    <span
      className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
        under ? "bg-emerald-500/15 text-emerald-300" : "bg-rose-500/15 text-rose-300"
      }`}
    >
      {typeof pct === "number"
        ? `${pct > 0 ? "+" : ""}${Math.round(pct)} %`
        : under ? t("widgets.deal") : t("widgets.overMarket")}
    </span>
  );
}

// V4.23 — central hit-count line: total / under / fair / over / no
// signal / shown. SAME numbers as Alerts, Explore and DealFlow.
function BreakdownLine({ bd }) {
  const { t } = useTranslation("ai");
  if (!bd || !bd.total) return null;
  const parts = [
    ["under", bd.under_market, "text-emerald-300"],
    ["fair", bd.fair, "text-zinc-300"],
    ["over", bd.over_market, "text-rose-300"],
    ["noSignal", bd.no_signal, "text-zinc-500"],
  ].filter(([, n]) => n > 0);
  return (
    <div
      data-testid="ai-widget-breakdown"
      className="flex flex-wrap items-center gap-x-2.5 gap-y-0.5 text-[10px] text-zinc-500"
    >
      <span className="font-semibold text-zinc-300">
        {t("widgets.breakdown.total", { count: bd.total })}
      </span>
      {parts.map(([key, n, cls]) => (
        <span key={key}>
          <span className={`font-semibold ${cls}`}>{n}</span>{" "}
          {t(`widgets.breakdown.${key}`)}
        </span>
      ))}
      {bd.shown != null && bd.shown < bd.total && (
        <span className="text-zinc-600">
          · {t("widgets.breakdown.shown", { count: bd.shown })}
        </span>
      )}
    </div>
  );
}

// V4.23 — compact premium card for the desktop canvas grid: image
// hierarchy first, then price + signal, core facts and location.
function CompactListingCard({ it }) {
  const { t } = useTranslation("ai");
  const candidates = (it.image_candidates && it.image_candidates.length
    ? it.image_candidates
    : [it.primary_image_url]
  ).filter(Boolean);
  const [idx, setIdx] = useState(0);
  const meta = [
    it.year,
    it.mileage_km ? `${Math.round(it.mileage_km / 1000)} tkm` : null,
    it.rooms ? `${it.rooms} ${t("widgets.roomsShort")}` : null,
    it.living_area_m2 ? `${Math.round(it.living_area_m2)} m²` : null,
  ].filter(Boolean).join(" · ");
  return (
    <Link
      to={`/listing/${it.id}`}
      data-testid={`ai-listing-card-${it.id}`}
      className={`group flex flex-col overflow-hidden rounded-xl border border-zinc-800 bg-zinc-900/70 transition hover:border-amber-500/50 ${
        it.offline ? "opacity-60" : ""
      }`}
    >
      {candidates.length && idx < candidates.length ? (
        <img
          src={candidates[idx]}
          alt=""
          loading="lazy"
          onError={() => setIdx(idx + 1)}
          className="aspect-[4/3] w-full object-cover"
        />
      ) : (
        <div className="flex aspect-[4/3] w-full flex-col items-center justify-center gap-1 bg-zinc-800">
          {it.vertical === "auto"
            ? <Car className="h-5 w-5 text-zinc-600" />
            : <Home className="h-5 w-5 text-zinc-600" />}
          <span className="text-[9px] text-zinc-600">{t("widgets.noImage")}</span>
        </div>
      )}
      <div className="space-y-0.5 p-2">
        <div className="flex items-center gap-1.5">
          <span className="text-sm font-semibold text-amber-400">
            {it.price_display || fmtEur(it.price)}
          </span>
          <SignalBadge signal={it.market_signal} pct={it.price_vs_market_pct} />
        </div>
        <div className="truncate text-xs text-zinc-200 transition group-hover:text-amber-300">
          {it.title}
        </div>
        <div className="truncate text-[10px] text-zinc-500">
          {[it.location, meta].filter(Boolean).join(" · ")}
        </div>
      </div>
    </Link>
  );
}

function AllOnMapLink({ widget }) {
  const { t } = useTranslation("ai");
  const params = widget.params || {};
  const shownCount = (widget.items || []).length;
  if (!Object.keys(params).length || !widget.total
      || widget.total <= shownCount) return null;
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== null && v !== undefined && v !== "") qs.set(k, String(v));
  });
  qs.set("src", "ai");
  return (
    <Link
      to={`/heatmap?${qs.toString()}`}
      data-testid="ai-widget-all-on-map"
      className="inline-flex items-center gap-1.5 text-[11px] text-amber-300/90 transition hover:text-amber-200"
    >
      <MapPin className="h-3 w-3" />
      {t("widgets.allOnMap", { count: widget.total })}
    </Link>
  );
}

function ListingsWidget({ widget, variant }) {
  const { t } = useTranslation("ai");
  const items = widget.items || [];
  if (!items.length) return null;
  const isPublic = variant === "public";
  const isCanvas = variant === "canvas";
  // P1.2 — optional stretch (over-budget) section rendered AFTER
  // the primary picks. Hidden on canvas + public variants (only
  // relevant in the chat conversation flow).
  const stretch = widget.stretch;
  const stretchItems = Array.isArray(stretch?.items) ? stretch.items : [];
  // V4.12 Phase F — premium mobile UX: horizontally scrollable snap
  // cards in the chat; V4.23 — compact 2-column grid on the canvas.
  // V4.14 — public share view: plain cards without app links.
  if (isCanvas) {
    return (
      <div data-testid="ai-widget-listings" className="space-y-1.5">
        <BreakdownLine bd={widget.signal_breakdown} />
        <div className="grid grid-cols-2 gap-2">
          {items.map((it) => <CompactListingCard key={it.id} it={it} />)}
        </div>
        <AllOnMapLink widget={widget} />
      </div>
    );
  }
  const gridCls = isPublic
    ? "grid grid-cols-1 gap-2 sm:grid-cols-2"
    : "flex gap-3 overflow-x-auto pb-1 snap-x sm:grid sm:grid-cols-2 sm:gap-2 sm:overflow-visible sm:pb-0";
  const cardCls = isPublic ? "" : "min-w-[270px] snap-start sm:min-w-0";
  const CardTag = isPublic ? "div" : Link;
  return (
    <div data-testid="ai-widget-listings" className="space-y-2">
      {widget.title && (
        <div className="text-xs text-zinc-500 uppercase tracking-wide">
          {widget.title_key
            ? t(`widgets.titles.${widget.title_key}`, { defaultValue: widget.title })
            : widget.title}
        </div>
      )}
      {!isPublic && <BreakdownLine bd={widget.signal_breakdown} />}
      <div className={gridCls}>
        {items.map((it) => (
          <CardTag
            key={it.id}
            {...(isPublic ? {} : { to: `/listing/${it.id}` })}
            data-testid={`ai-listing-card-${it.id}`}
            className={`flex gap-3 rounded-xl border border-zinc-800 bg-zinc-900/70 p-2 transition group ${
              isPublic ? "" : "hover:border-amber-500/50"
            } ${it.offline ? "opacity-60" : ""} ${cardCls}`}
          >
            <ListingThumb it={it} />
            <div className="min-w-0 flex-1">
              <div className="text-sm text-zinc-100 truncate group-hover:text-amber-300 transition">
                {it.title}
              </div>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="text-sm font-semibold text-amber-400">{it.price_display || fmtEur(it.price)}</span>
                <SignalBadge signal={it.market_signal} pct={it.price_vs_market_pct} />
                {it.offline && (
                  <span
                    data-testid={`ai-listing-offline-${it.id}`}
                    className="rounded bg-zinc-800 px-1.5 py-0.5 text-[9px] font-semibold uppercase text-zinc-400"
                  >
                    {t("widgets.inactive")}
                  </span>
                )}
              </div>
              <div className="text-xs text-zinc-500 truncate mt-0.5">
                {[
                  it.location,
                  it.year,
                  it.mileage_km ? `${Math.round(it.mileage_km / 1000)} tkm` : null,
                  it.rooms ? `${it.rooms} ${t("widgets.roomsShort")}` : null,
                  it.living_area_m2 ? `${Math.round(it.living_area_m2)} m²` : null,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </div>
            </div>
          </CardTag>
        ))}
      </div>
      {!isPublic && !isCanvas && stretchItems.length > 0 && (
        <StretchSection widget={widget} items={stretchItems} />
      )}
    </div>
  );
}

// P1.2 — Secondary "etwas über deinem Budget" section. Never mixed
// with the primary picks; each card carries an explicit +€X / +Y %
// delta chip and lists the concrete signals (Standzeit, prior price
// reduction, still under market) that make negotiation plausible.
// Wording avoids promises — always "eventuell", never "sicher".
function StretchSection({ widget, items }) {
  const { t } = useTranslation("ai");
  const fmtEurLocal = (v) =>
    typeof v === "number" && Number.isFinite(v)
      ? `€${Math.round(v).toLocaleString("de-AT")}`
      : "—";
  return (
    <div
      data-testid="ai-widget-listings-stretch"
      className="mt-3 rounded-xl border border-amber-500/25 bg-amber-500/[0.06] p-3 space-y-2"
    >
      <div className="text-[10px] uppercase tracking-widest font-bold text-amber-300">
        {t("stretch.headline", {
          defaultValue: "Etwas über deinem Budget — eventuell verhandelbar",
        })}
      </div>
      <div className="text-[11px] text-zinc-400">
        {t("stretch.body", {
          defaultValue:
            "Diese Inserate liegen knapp über deinem Limit. Ob sich der Preis verhandeln lässt, entscheidet der Verkäufer — die Signale unten sind Anhaltspunkte, keine Zusagen.",
        })}
      </div>
      <div className="flex flex-col gap-2">
        {items.map((it) => (
          <Link
            to={`/listing/${it.id}`}
            key={it.id}
            data-testid={`ai-listing-stretch-card-${it.id}`}
            className="flex flex-col gap-1 rounded-lg border border-amber-500/25 bg-zinc-900/70 p-2 hover:border-amber-400/60 transition"
          >
            <div className="flex items-center justify-between gap-2">
              <div className="text-sm text-zinc-100 truncate">{it.title}</div>
              <span
                data-testid={`ai-listing-stretch-delta-${it.id}`}
                className="shrink-0 rounded bg-amber-500/15 border border-amber-500/40 px-1.5 py-0.5 text-[10px] font-semibold text-amber-200"
              >
                +{fmtEurLocal(it.over_budget_eur)} · +{it.over_budget_pct?.toFixed?.(1) ?? it.over_budget_pct} %
              </span>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <span className="text-amber-400 font-semibold">{fmtEurLocal(it.price)}</span>
              <SignalBadge signal={it.market_signal} pct={it.vs_market_pct} />
            </div>
            {Array.isArray(it.negotiation_hints) && it.negotiation_hints.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-0.5">
                {it.negotiation_hints.map((h) => (
                  <span
                    key={h}
                    className="rounded bg-zinc-800 px-1.5 py-0.5 text-[10px] text-zinc-400"
                  >
                    {t(`stretch.hint.${h}`, { defaultValue: h })}
                  </span>
                ))}
              </div>
            )}
          </Link>
        ))}
      </div>
    </div>
  );
}

function StatRow({ label, value }) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-zinc-500">{label}</span>
      <span className="text-zinc-100 font-medium">{value}</span>
    </div>
  );
}

function RangeBar({ stats }) {
  // min → p25 → median → p75 → max as a horizontal band; the p25–p75
  // core range is highlighted, the median gets a marker.
  const { min, p25, median, p75, max } = stats || {};
  if (min == null || max == null || max <= min) return null;
  const pos = (v) => `${Math.max(0, Math.min(100, ((v - min) / (max - min)) * 100))}%`;
  return (
    <div className="pt-1.5" data-testid="ai-widget-range-bar">
      <div className="relative h-2 rounded-full bg-zinc-800">
        <div
          className="absolute h-2 rounded-full bg-amber-500/40"
          style={{ left: pos(p25), width: `calc(${pos(p75)} - ${pos(p25)})` }}
        />
        <div
          className="absolute -top-0.5 h-3 w-0.5 rounded bg-amber-400"
          style={{ left: pos(median) }}
        />
      </div>
      <div className="mt-1 flex justify-between text-[10px] text-zinc-600">
        <span>{fmtEur(min)}</span>
        <span>{fmtEur(max)}</span>
      </div>
    </div>
  );
}

function PriceEstimateWidget({ widget }) {
  const { t } = useTranslation("ai");
  const st = widget.stats || {};
  return (
    <div data-testid="ai-widget-price-estimate" className="rounded-xl border border-zinc-800 bg-zinc-900/70 p-3 space-y-1.5 max-w-sm">
      <div className="flex items-center gap-2 text-xs text-zinc-500 uppercase tracking-wide">
        <BarChart3 className="h-3.5 w-3.5" /> {t("widgets.marketPrice")} {widget.label}
      </div>
      <div className="text-2xl font-semibold text-amber-400">{fmtEur(st.average ?? st.median)}</div>
      <RangeBar stats={st} />
      <StatRow label={t("widgets.usualRange")} value={`${fmtEur(st.p25)} – ${fmtEur(st.p75)}`} />
      <div className="text-xs text-zinc-500 pt-1">
        {t("widgets.basisActive", { count: st.count })}
        {widget.relaxation_key
          ? ` (${t(`widgets.relaxation.${widget.relaxation_key}`, { defaultValue: widget.relaxation })})`
          : widget.relaxation ? ` (${widget.relaxation})` : ""}
      </div>
    </div>
  );
}

function RegionPriceWidget({ widget }) {
  const { t } = useTranslation("ai");
  const p = widget.price_stats || {};
  const sqm = widget.sqm_stats;
  const isRent = widget.transaction_type === "rent";
  return (
    <div data-testid="ai-widget-region-price" className="rounded-xl border border-zinc-800 bg-zinc-900/70 p-3 space-y-1.5 max-w-sm">
      <div className="text-xs text-zinc-500 uppercase tracking-wide">
        {isRent ? t("widgets.rent") : t("widgets.buyPrice")} · {widget.region}
      </div>
      <div className="text-2xl font-semibold text-amber-400">
        {fmtEur(p.average ?? p.median)}
        {isRent && <span className="text-sm text-zinc-500"> {t("widgets.perMonth")}</span>}
      </div>
      {sqm && <StatRow label={t("widgets.medianSqm")} value={fmtEur(sqm.average ?? sqm.median)} />}
      <StatRow label={t("widgets.range")} value={`${fmtEur(p.p25)} – ${fmtEur(p.p75)}`} />
      <div className="text-xs text-zinc-500 pt-1">{t("widgets.basisActive", { count: p.count })}</div>
    </div>
  );
}

function RegionStatsWidget({ widget }) {
  const { t } = useTranslation("ai");
  return (
    <div data-testid="ai-widget-region-stats" className="rounded-xl border border-zinc-800 bg-zinc-900/70 p-3 space-y-1.5 max-w-sm">
      <div className="text-xs text-zinc-500 uppercase tracking-wide">{t("widgets.region")} · {widget.region}</div>
      {widget.buy_median_price_per_m2 && (
        <StatRow label={`${t("widgets.buySqm")} (${widget.buy_sample})`} value={fmtEur(widget.buy_median_price_per_m2)} />
      )}
      {widget.rent_median_price_per_m2 && (
        <StatRow label={`${t("widgets.rentSqm")} (${widget.rent_sample})`} value={fmtEur(widget.rent_median_price_per_m2)} />
      )}
      {isPlausibleYield(widget.gross_rental_yield_pct) && (
        <div className="flex justify-between text-sm">
          <span className="text-zinc-500 flex items-center gap-1">
            <TrendingUp className="h-3.5 w-3.5" /> {t("widgets.grossYield")}
          </span>
          <span className="text-emerald-400 font-semibold">{widget.gross_rental_yield_pct} %</span>
        </div>
      )}
    </div>
  );
}

function MarketOverviewWidget({ widget }) {
  const { t } = useTranslation("ai");
  return (
    <div data-testid="ai-widget-market-overview" className="rounded-xl border border-zinc-800 bg-zinc-900/70 p-3 space-y-1.5 max-w-sm">
      <div className="text-xs text-zinc-500 uppercase tracking-wide">
        {t("widgets.marketState")} · {widget.vertical === "auto" ? t("widgets.cars") : t("widgets.realEstate")}
        {widget.country ? ` · ${widget.country}` : ""}
      </div>
      <StatRow label={t("widgets.activeListings")} value={widget.total_active?.toLocaleString("de-AT")} />
      <StatRow label={t("widgets.underMarket")} value={widget.under_market?.toLocaleString("de-AT")} />
      <StatRow label={t("widgets.marketFair")} value={widget.market_fair?.toLocaleString("de-AT")} />
      <StatRow label={t("widgets.overMarketFull")} value={widget.over_market?.toLocaleString("de-AT")} />
      {widget.unrated != null && (
        <StatRow label={t("widgets.unrated")} value={widget.unrated?.toLocaleString("de-AT")} />
      )}
      {widget.median_price != null && <StatRow label={t("widgets.medianPrice")} value={fmtEur(widget.median_price)} />}
      {widget.unrated != null && (
        <div className="text-xs text-zinc-500 pt-1" data-testid="ai-widget-market-overview-note">
          {t("widgets.unratedNote")}
        </div>
      )}
    </div>
  );
}

function HeatmapLinkWidget({ widget }) {
  const { t } = useTranslation("ai");
  const [lat, lng] = widget.center || [];
  if (lat == null) return null;
  return (
    <Link
      to={`/heatmap?c=${lat},${lng}&z=${widget.zoom || 11}`}
      data-testid="ai-widget-heatmap-link"
      className="inline-flex items-center gap-2 rounded-xl border border-amber-500/40 bg-amber-500/10 px-3.5 py-2 text-sm text-amber-300 hover:bg-amber-500/20 transition"
    >
      <MapPin className="h-4 w-4" />
      {t("widgets.openHeatmap")}: {widget.region}
    </Link>
  );
}

// V4.12 Phase C/E — AI → Heatmap handover: opens the map with the EXACT
// canonical filters of the last search (same vocabulary, one system),
// plus one-click "save as alert" on the same criteria.
function MapSearchLinkWidget({ widget }) {
  const { t } = useTranslation("ai");
  const [alertOpen, setAlertOpen] = useState(false);
  const params = widget.params || {};
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== null && v !== undefined && v !== "") qs.set(k, String(v));
  });
  qs.set("src", "ai");
  const filters = {};
  ["makes", "models", "fuels", "gearboxes", "body_types", "features"].forEach((k) => {
    if (params[k]) filters[k] = String(params[k]).split(",");
  });
  ["year_min", "year_max", "km_max", "price_min", "price_max"].forEach((k) => {
    if (params[k] !== null && params[k] !== undefined && params[k] !== "") {
      filters[k] = Number(params[k]);
    }
  });
  if (params.tt) filters.transaction_type = params.tt;
  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      <Link
        to={`/heatmap?${qs.toString()}`}
        data-testid="ai-widget-map-search-link"
        className="inline-flex items-center gap-2 rounded-xl border border-amber-500/40 bg-amber-500/10 px-3.5 py-2 text-sm text-amber-300 hover:bg-amber-500/20 transition"
      >
        <MapPin className="h-4 w-4" />
        {t("widgets.viewOnMap")}{widget.label ? `: ${widget.label}` : ""}
      </Link>
      <button
        type="button"
        data-testid="ai-save-alert-button"
        onClick={() => setAlertOpen(true)}
        className="inline-flex items-center gap-2 rounded-xl border border-zinc-700 bg-zinc-900 px-3.5 py-2 text-sm text-zinc-200 hover:border-amber-500/50 hover:text-amber-300 transition"
      >
        <Bell className="h-4 w-4" />
        {t("widgets.saveAlert")}
      </button>
      <SaveAlertDialog
        open={alertOpen}
        onOpenChange={setAlertOpen}
        vertical={widget.vertical === "auto" ? "auto" : "immo"}
        filters={filters}
        bbox={null}
        label={widget.label}
        source="ai"
      />
    </span>
  );
}

function RegionCompareWidget({ widget }) {
  const { t } = useTranslation("ai");
  const rows = widget.rows || [];
  if (!rows.length) return null;
  return (
    <div data-testid="ai-widget-region-compare" className="rounded-xl border border-zinc-800 bg-zinc-900/70 p-3 overflow-x-auto">
      <div className="text-xs text-zinc-500 uppercase tracking-wide mb-2">{t("widgets.regionCompare")}</div>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-zinc-500">
            <th className="pb-1.5 pr-3 font-normal">{t("widgets.region")}</th>
            <th className="pb-1.5 pr-3 font-normal">{t("widgets.buySqm")}</th>
            <th className="pb-1.5 pr-3 font-normal">{t("widgets.rentSqm")}</th>
            <th className="pb-1.5 font-normal">{t("widgets.yield")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.region} className="border-t border-zinc-800/70">
              <td className="py-1.5 pr-3 text-zinc-100 font-medium">{r.region}</td>
              <td className="py-1.5 pr-3 text-zinc-300">
                {r.buy_median_price_per_m2 ? fmtEur(r.buy_median_price_per_m2) : "—"}
                {r.buy_sample ? <span className="text-zinc-600 text-xs"> ({r.buy_sample})</span> : null}
              </td>
              <td className="py-1.5 pr-3 text-zinc-300">
                {r.rent_median_price_per_m2 ? fmtEur(r.rent_median_price_per_m2) : "—"}
                {r.rent_sample ? <span className="text-zinc-600 text-xs"> ({r.rent_sample})</span> : null}
              </td>
              <td className="py-1.5">
                {isPlausibleYield(r.gross_rental_yield_pct) ? (
                  <span className="text-emerald-400 font-semibold">{r.gross_rental_yield_pct} %</span>
                ) : (
                  "—"
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const RENDERERS = {
  listings: ListingsWidget,
  price_estimate: PriceEstimateWidget,
  region_price: RegionPriceWidget,
  region_stats: RegionStatsWidget,
  region_compare: RegionCompareWidget,
  market_overview: MarketOverviewWidget,
  heatmap_link: HeatmapLinkWidget,
  map_search_link: MapSearchLinkWidget,
  // V4.12 Phase F — canvas visuals + comparison.
  mini_map: MiniMapWidget,
  price_distribution: PriceDistributionWidget,
  scatter: ScatterPlotWidget,
  listing_compare: ListingCompareWidget,
  // V4.23 — dynamic result-set groups.
  listing_groups: ListingGroupsWidget,
  // NoVA Welle A — deterministic AT vehicle-import breakdown (legacy turns).
  vehicle_import_breakdown: VehicleImportWidget,
  // Calculation Canvas — ONE generic renderer for all AI calculations.
  calculation: CalculationWidget,
  // Klagenfurt P0 2026-08 — server-built deal-comparables widget for
  // the DealAdvisor chat (purchase + rental + excluded, strictly
  // separated, internal navigation, no LLM-invented data).
  deal_comparables: DealComparablesChatWidget,
  // Master-Hotfix P0 — manual entry as first-class fallback for blocked links.
  link_manual_fallback: ManualVehicleEntryCard,
};

// Shared with the CanvasBoard — ONE renderer registry for chat-inline
// (mobile) and workspace (desktop).
export const WIDGET_RENDERERS = RENDERERS;

// Conversational CTAs stay inline in the chat on every breakpoint;
// data widgets move to the canvas on xl+ (variant="chat").
const CHAT_INLINE_ALWAYS = new Set([
  "map_search_link",
  "heatmap_link",
  "vehicle_import_breakdown",
  "calculation",
  "deal_comparables",
  "link_manual_fallback",
]);

// V4.23 — chat/canvas separation: on desktop the big data widgets live
// ONLY in the canvas; the chat shows a compact reference chip instead
// ("Im Canvas: 12 Inserate") so nothing appears twice.
function canvasRefText(w, t) {
  if (w.type === "listings") {
    const n = w.total || (w.items || []).length;
    return t("widgets.canvasRef.listings", { count: n });
  }
  if (w.type === "listing_groups") {
    return t("widgets.canvasRef.groups", { count: (w.groups || []).length });
  }
  if (w.type === "listing_compare") {
    return t("widgets.canvasRef.compare", { count: (w.items || []).length });
  }
  const key = w.type === "scatter"
    ? (w.x_key === "mileage_km" ? "scatter_auto" : "scatter_immo")
    : w.type;
  return t(`canvas.widgetTitles.${key}`, { defaultValue: t("canvas.widgetTitles.generic") });
}

function CanvasRefChip({ widget }) {
  const { t } = useTranslation("ai");
  return (
    <span
      data-testid={`ai-canvas-ref-${widget.type}`}
      className="hidden items-center gap-1.5 rounded-lg border border-amber-500/20 bg-amber-500/5 px-2.5 py-1.5 text-[11px] text-amber-300/90 xl:inline-flex"
    >
      <PanelRight className="h-3 w-3" />
      {t("widgets.inCanvas")}: {canvasRefText(widget, t)}
    </span>
  );
}

export function ChatWidgets({ widgets, variant, onSend, conversationId }) {
  if (!widgets || !widgets.length) return null;
  return (
    <div className="mt-2.5 min-w-0 max-w-full space-y-2.5" data-testid="ai-chat-widgets">
      {widgets.map((w, i) => {
        const R = RENDERERS[w.type];
        if (!R) return null;
        const hideOnDesktop =
          variant === "chat" && !CHAT_INLINE_ALWAYS.has(w.type);
        // P0.4c Punkt 3 — inject the current advisor conversation-id
        // into the widget payload so a comparable click can save the
        // return state without threading extra props through every
        // renderer signature.
        const widgetWithConv =
          conversationId && !w.conversation_id
            ? { ...w, conversation_id: conversationId }
            : w;
        return (
          <React.Fragment key={`${w.type}-${i}`}>
            <div className={`min-w-0 ${hideOnDesktop ? "xl:hidden" : ""}`}>
              <R widget={widgetWithConv} variant={variant} onSend={onSend} />
            </div>
            {hideOnDesktop && <CanvasRefChip widget={widgetWithConv} />}
          </React.Fragment>
        );
      })}
    </div>
  );
}
